import type { MeResponse } from '@greenroom/shared'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState, type FormEvent, type ReactNode } from 'react'
import { Link, Navigate, useNavigate, useParams, useSearchParams } from 'react-router'
import { Logo } from '../components/logo'
import { Button, LinkButton } from '../components/ui/button'
import { Field } from '../components/ui/field'
import { FullPageSpinner } from '../components/ui/spinner'
import { ErrorNote } from '../components/ui/misc'
import { api, errorMessage, fieldErrors } from '../lib/api'
import { meQuery, safeNext, selectTeam, useMe } from '../lib/auth'
import { q } from '../lib/queries'
import { useTitle } from '../lib/title'

function AuthLayout({ title, subtitle, children, footer }: { title: string; subtitle: string; children: ReactNode; footer?: ReactNode }) {
  return (
    <div className="flex min-h-full flex-col items-center justify-center px-4 py-12">
      <Logo className="mb-10" />
      <div className="card w-full max-w-sm p-7">
        <h1 className="text-xl font-semibold">{title}</h1>
        <p className="mt-1 mb-6 text-sm text-muted">{subtitle}</p>
        {children}
      </div>
      {footer && <p className="mt-6 text-sm text-muted">{footer}</p>}
    </div>
  )
}

/**
 * Shared by both forms: posts, stores the user in the cache and continues to where the visitor wanted
 * to go, or to where `land` sends them.
 */
function useAuthSubmit(path: '/api/auth/login' | '/api/auth/signup', land?: (me: MeResponse, next: string) => string) {
  const qc = useQueryClient()
  const navigate = useNavigate()
  const [params] = useSearchParams()
  const next = safeNext(params.get('next'))
  const mutation = useMutation({
    mutationFn: (body: Record<string, string>) => api<MeResponse>(path, { body }),
    meta: { silent: true },
    onSuccess: (me) => {
      qc.setQueryData(meQuery.queryKey, me)
      navigate(land ? land(me, next) : next, { replace: true })
    },
  })
  return { mutation, next, nextQuery: params.get('next') ? `?next=${encodeURIComponent(next)}` : '' }
}

export function LoginPage() {
  useTitle('Log in')
  const me = useMe()
  const { mutation, next, nextQuery } = useAuthSubmit('/api/auth/login')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  if (me.isPending) return <FullPageSpinner />
  if (me.data) return <Navigate to={next} replace />

  const submit = (e: FormEvent) => {
    e.preventDefault()
    mutation.mutate({ email, password })
  }
  return (
    <AuthLayout
      title="Welcome back"
      subtitle="Log in to your studios and recordings."
      footer={
        <>
          No account yet?{' '}
          <Link to={`/signup${nextQuery}`} className="font-medium text-accent hover:underline">
            Sign up
          </Link>
        </>
      }
    >
      <form onSubmit={submit} className="space-y-4">
        <Field label="Email" type="email" autoComplete="email" value={email} onChange={(e) => setEmail(e.target.value)} required autoFocus />
        <Field label="Password" type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} required />
        {mutation.error && <ErrorNote>{errorMessage(mutation.error)}</ErrorNote>}
        <Button type="submit" variant="primary" className="w-full" loading={mutation.isPending}>
          Log in
        </Button>
        <p className="text-center text-[13px] text-faint">Forgot your password? Ask an owner of your team for a reset link.</p>
      </form>
    </AuthLayout>
  )
}

const inviteIn = (next: string) => /^\/invite\/([\w-]+)$/.exec(next)?.[1]

/** Signing up through an invite uses it up, so its page is no place to land on: go straight into the team. */
function landAfterSignup(me: MeResponse, next: string) {
  const team = me.teams[0]
  if (!inviteIn(next) || !team) return next
  selectTeam(team.id)
  return '/studios'
}

export function SignupPage() {
  useTitle('Sign up')
  const me = useMe()
  const config = useQuery(q.authConfig)
  const { mutation, next, nextQuery } = useAuthSubmit('/api/auth/signup', landAfterSignup)
  const inviteToken = inviteIn(next)
  const invite = useQuery({ ...q.invite(inviteToken ?? ''), enabled: !!inviteToken })
  const [form, setForm] = useState({ name: '', email: '', password: '', code: '' })
  if (me.isPending || config.isPending) return <FullPageSpinner />
  // only for visitors who arrive signed in: after a sign-up, onSuccess decides where to go, and this
  // redirect would otherwise overtake it and land the new member on the invite they just used up
  if (me.data && mutation.isIdle) return <Navigate to={next} replace />

  const needsCode = !inviteToken && config.data?.signup === 'invite'
  const errors = fieldErrors(mutation.error)
  const generalError = mutation.error && Object.keys(errors).length === 0 ? errorMessage(mutation.error) : null
  const set = (key: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement>) => setForm({ ...form, [key]: e.target.value })
  const submit = (e: FormEvent) => {
    e.preventDefault()
    const { code, ...account } = form
    mutation.mutate({ ...account, ...(inviteToken ? { invite: inviteToken } : {}), ...(needsCode ? { code } : {}) })
  }
  return (
    <AuthLayout
      title="Create your account"
      subtitle={
        invite.data
          ? `${invite.data.invitedBy} invited you to ${invite.data.teamName}.`
          : 'Record in studio quality, with every guest uploading their own tracks.'
      }
      footer={
        <>
          Already have an account?{' '}
          <Link to={`/login${nextQuery}`} className="font-medium text-accent hover:underline">
            Log in
          </Link>
        </>
      }
    >
      <form onSubmit={submit} className="space-y-4">
        <Field label="Your name" autoComplete="name" value={form.name} onChange={set('name')} error={errors.name} maxLength={60} required autoFocus />
        <Field label="Email" type="email" autoComplete="email" value={form.email} onChange={set('email')} error={errors.email} required />
        <Field
          label="Password"
          type="password"
          autoComplete="new-password"
          value={form.password}
          onChange={set('password')}
          error={errors.password}
          hint="At least 10 characters."
          minLength={10}
          required
        />
        {needsCode && (
          <Field
            label="Sign-up code"
            autoComplete="off"
            value={form.code}
            onChange={set('code')}
            error={errors.code}
            hint="Greenroom is invite-only. Without an invite link from a team, you need a sign-up code."
            required
          />
        )}
        {generalError && <ErrorNote>{generalError}</ErrorNote>}
        <Button type="submit" variant="primary" className="w-full" loading={mutation.isPending}>
          Create account
        </Button>
      </form>
    </AuthLayout>
  )
}

/** Opened from a link a team owner created; there is no email, so the owner hands it over. */
export function ResetPasswordPage() {
  useTitle('Reset password')
  const { token = '' } = useParams()
  const reset = useQuery(q.passwordReset(token))
  const qc = useQueryClient()
  const navigate = useNavigate()
  const [password, setPassword] = useState('')
  const mutation = useMutation({
    mutationFn: () => api<MeResponse>(`/api/password-resets/${token}`, { body: { password } }),
    meta: { silent: true },
    onSuccess: (me) => {
      // whoever was signed in on this browser before is not who is signed in now
      qc.clear()
      qc.setQueryData(meQuery.queryKey, me)
      navigate('/studios', { replace: true })
    },
  })
  if (reset.isPending) return <FullPageSpinner />
  if (reset.isError || reset.data.expired) {
    return (
      <AuthLayout
        title="This reset link does not work"
        subtitle={
          reset.isError
            ? `${errorMessage(reset.error)}. Ask an owner of your team for a new one.`
            : 'It was used already or is older than 24 hours. Ask an owner of your team for a new one.'
        }
      >
        <LinkButton to="/login" variant="primary" className="w-full">
          Go to log in
        </LinkButton>
      </AuthLayout>
    )
  }

  const errors = fieldErrors(mutation.error)
  const generalError = mutation.error && !errors.password ? errorMessage(mutation.error) : null
  const submit = (e: FormEvent) => {
    e.preventDefault()
    mutation.mutate()
  }
  return (
    <AuthLayout title="Set a new password" subtitle={`For ${reset.data.name} (${reset.data.email}). You are signed in right after, and signed out on every other device.`}>
      <form onSubmit={submit} className="space-y-4">
        {/* lets password managers store the new password under the right account */}
        <input type="email" name="username" autoComplete="username" value={reset.data.email} readOnly className="hidden" />
        <Field
          label="New password"
          type="password"
          autoComplete="new-password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          error={errors.password}
          hint="At least 10 characters."
          minLength={10}
          required
          autoFocus
        />
        {generalError && <ErrorNote>{generalError}</ErrorNote>}
        <Button type="submit" variant="primary" className="w-full" loading={mutation.isPending}>
          Save and log in
        </Button>
      </form>
    </AuthLayout>
  )
}
